import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ToastService } from '../../ui/toast.service';

/**
 * EB-3796: Detects whether a newer AMIE deploy is available and prompts the
 * user to reload. Keeps long-running tabs in sync with what's currently on
 * S3 + CloudFront.
 *
 * Strategy:
 * - `currentVersion` is baked in at build time via `environment.appVersion`
 *   (replaced from `VERSION_PLACEHOLDER` by `inject-version`)
 * - `/version.json` is written into the dist by the same `inject-version`
 *   step and served fresh by Nginx (Cache-Control: no-cache)
 * - We poll on init, on `window:focus`, and every 10 minutes
 * - When `latest !== current` we open a persistent toast with a Reload action
 *
 * Local dev (`appVersion === '0.0.0-local'`) skips polling entirely so
 * `nx serve` doesn't fail on a missing /version.json or pop a spurious
 * toast against the test/prod manifest if a deploy happens to ship.
 */
@Injectable({ providedIn: 'root' })
export class VersionService {
  /** Stable URL — served from the dist root by Nginx, no auth required. */
  private static readonly VERSION_URL = '/version.json';
  /** Local dev sentinel — environment.ts ships this literal; replaced on deploy. */
  private static readonly LOCAL_VERSION = '0.0.0-local';
  /** Background poll cadence. 10 min keeps it cheap (~6 req/h) yet snappy. */
  private static readonly POLL_INTERVAL_MS = 10 * 60 * 1000;

  private readonly http = inject(HttpClient);
  private readonly toastService = inject(ToastService);

  /** Build-time version baked into the running bundle. */
  readonly currentVersion = signal<string>(environment.appVersion);
  /** Latest version observed on the server. Null until first successful poll. */
  readonly latestVersion = signal<string | null>(null);

  /**
   * True once the server reports a different version than the one running.
   * Guarded against the initial null state so we don't trigger before the
   * first poll completes.
   */
  readonly updateAvailable = computed(() => {
    const latest = this.latestVersion();
    const current = this.currentVersion();
    return latest !== null && latest !== current;
  });

  /** Latch — keeps the toast from re-popping after the user dismisses it. */
  private toastShown = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private focusHandler: (() => void) | null = null;

  constructor() {
    // React to `updateAvailable` flipping true. Set up here so the effect
    // is in injection context; the actual toast only fires once polling
    // has produced a different version.
    effect(() => {
      if (this.updateAvailable() && !this.toastShown) {
        this.toastShown = true;
        this.openUpdateToast();
      }
    });
  }

  /**
   * Begin polling. Called by app bootstrap AFTER `AuthService.configure()`
   * completes — keeps the initial `/version.json` request out of the
   * critical-path auth flow.
   *
   * Idempotent: calling twice is a no-op.
   */
  start(): void {
    if (this.pollTimer !== null) return;
    // Local dev: nothing to poll against — `nx serve` has no /version.json.
    if (this.currentVersion() === VersionService.LOCAL_VERSION) return;

    this.checkNow();
    this.pollTimer = setInterval(() => this.checkNow(), VersionService.POLL_INTERVAL_MS);

    // Re-check immediately when the tab regains focus — the most common
    // "I came back from lunch, my session was idle" trigger.
    this.focusHandler = (): void => this.checkNow();
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this.focusHandler);
    }
  }

  /** Test-friendly tear-down. Released listeners + timer, idempotent. */
  stop(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.focusHandler !== null && typeof window !== 'undefined') {
      window.removeEventListener('focus', this.focusHandler);
      this.focusHandler = null;
    }
  }

  /**
   * One-shot fetch of `/version.json`. Exposed so the update-toast (or
   * manual debug) can force a re-check. Errors are swallowed silently —
   * we never want a transient network failure to pop a "something broke"
   * notification on top of an otherwise-healthy app.
   */
  checkNow(): void {
    this.http
      .get<{ version?: string; buildTime?: string }>(VersionService.VERSION_URL, {
        // Defense-in-depth — Nginx already says no-cache for *.json, but
        // intermediaries (corporate proxies) sometimes ignore it.
        headers: { 'Cache-Control': 'no-cache' },
      })
      .pipe(catchError(() => of(null)))
      .subscribe((data) => {
        if (data?.version) {
          this.latestVersion.set(data.version);
        }
      });
  }

  private openUpdateToast(): void {
    this.toastService.open(
      'A new version of AMIE is available.',
      'Reload now',
      {
        duration: 0, // persistent — only the action button dismisses it
        tone: 'default',
        onAction: () => {
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        },
        onTimeout: () => {
          // Persistent toast never times out, but if someone changes the
          // default it shouldn't re-fire. Latch stays set either way.
        },
      },
    );
  }
}
